// src/checklists/outbox.js — offline write queue for checklist submissions (v130.39).
//
// Compliance-path reliability, and the sibling of src/clock/outbox.js (v124.10).
// A clock-in that fails because the DEVICE is offline is queued and replayed; a
// CHECKLIST that fails the same way was simply lost, and the store had to answer
// all 29 items again. That asymmetry is the bug:
//
//   Dubai - Meadows Town Center, 22 Aug 2026 — the opening checklist never reached
//   the server (nor the closing on the 21st). v130.38 stopped the app claiming those
//   were submitted; this stops them being lost in the first place.
//
// REPLAY IS NOT AN UPSERT — this is the one real difference from the clock queue.
// The clock write upserts on (staff_id, date, seq), so a re-send rewrites the same
// row. A checklist POST is an INSERT against UNIQUE (store, date, type). So a replay
// of an entry that DID land the first time comes back 409 / duplicate key. That is
// not a failure: it means the record is on file, which is precisely what we wanted.
// isAlreadyFiled() treats it as success and drops the entry.
//
// ONLY OFFLINE FAILURES ARE QUEUED: db.js distinguishes a network failure
// (DB.isOnline() false) from a server rejection. A server "no" — including the
// cross-device duplicate a colleague already filed — still surfaces to the user.
//
// SECURITY: a queued entry NEVER contains the PIN. DB.checklists.save() attaches
// live auth via authFields() at call time, so nothing credential-bearing is parked
// on the device. If auth is unavailable at replay the entry stays queued and drains
// once someone next verifies a PIN.

const OUTBOX_KEY = 'checklist_outbox_v1';
// Entries the SERVER permanently rejects. Without a dead letter, drain() would retry a
// poison payload forever and the "waiting to sync" indicator would never clear — the
// failure mode the clock queue hit at Marina Mall (v124.26). Moved, never deleted: the
// answers are preserved so they can be re-entered or recovered.
const DEAD_KEY = 'checklist_outbox_dead_v1';
const MAX_REPLAY_ATTEMPTS = 10;

// ── Pure helpers (exported for tests) ──────────────────────────────────────

// One queued entry per (store, date, type) — exactly the server's UNIQUE.
// A store re-submitting the same checklist while the first is still queued
// replaces it rather than queueing two that must conflict on arrival.
export function outboxKey(entry) {
  return String((entry && entry.store) || '') + '|' +
         String((entry && entry.date) || '') + '|' +
         String((entry && entry.type) || '');
}

// Queue bookkeeping must never reach the server.
export function stripQueueMeta(entry) {
  if (!entry || typeof entry !== 'object') return entry;
  const out = {};
  for (const k of Object.keys(entry)) {
    if (k === '_queued_at' || k === '_attempts' || k === '_last_error') continue;
    out[k] = entry[k];
  }
  return out;
}

// Belt and braces: a PIN must never be persisted, even if a caller passes one.
export function stripForQueue(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = {};
  for (const k of Object.keys(payload)) {
    if (k === 'auth_pin' || k === 'auth_staff_id') continue;
    out[k] = payload[k];
  }
  return out;
}

export function upsertQueue(list, entry) {
  const key = outboxKey(entry);
  const out = (list || []).filter((e) => outboxKey(e) !== key);
  out.push(entry);
  return out;
}

// A duplicate on replay means the record is ALREADY ON FILE. That is the outcome we
// wanted, so it counts as drained rather than as an error. Mirrors the duplicate
// detection in submitChecklist (v86.16).
export function isAlreadyFiled(err) {
  const msg = String((err && err.message) || err || '');
  return /\b409\b/.test(msg) || /duplicate key/i.test(msg) || /checklists_store_date_type/i.test(msg);
}

// ── Storage ────────────────────────────────────────────────────────────────

function read() {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) { return []; }
}

// v136.8: write() says whether it STORED the list (it used to swallow a full device's error,
// so saveChecklist said "queued" for a checklist that was never kept). On a refused write the
// per-view offline caches are cleared (DB.freeCacheSpace) and the write is tried once more.
function write(list) {
  const v = JSON.stringify(list || []);
  try { localStorage.setItem(OUTBOX_KEY, v); return true; } catch (e) { /* full: free space below */ }
  try {
    if (typeof DB !== 'undefined' && DB && typeof DB.freeCacheSpace === 'function') DB.freeCacheSpace();
  } catch (e) { /* best-effort */ }
  try { localStorage.setItem(OUTBOX_KEY, v); return true; } catch (e) { return false; }
}

export function pending() { return read(); }
export function pendingCount() { return read().length; }

function enqueue(payload) {
  const entry = stripForQueue(payload);
  entry._queued_at = new Date().toISOString();
  entry._attempts = 0;
  return write(upsertQueue(read(), entry));
}

function removeKey(key) {
  write(read().filter((e) => outboxKey(e) !== key));
}

function bumpAttempts(key, err) {
  const list = read();
  const hit = list.find((e) => outboxKey(e) === key);
  if (!hit) return 0;
  hit._attempts = (hit._attempts || 0) + 1;
  hit._last_error = String((err && err.message) || err || '').slice(0, 200);
  write(list);
  return hit._attempts;
}

function deadLetter(entry) {
  try {
    const raw = localStorage.getItem(DEAD_KEY);
    const dead = raw ? JSON.parse(raw) : [];
    dead.push(entry);
    localStorage.setItem(DEAD_KEY, JSON.stringify(dead));
  } catch (e) { /* best effort */ }
  removeKey(outboxKey(entry));
}

export function pendingDead() {
  try {
    const raw = localStorage.getItem(DEAD_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) { return []; }
}
export function deadLetterCount() { return pendingDead().length; }
export function dismissDead() {
  try { localStorage.removeItem(DEAD_KEY); } catch (e) {}
}

// ── Save ───────────────────────────────────────────────────────────────────

// Drop-in for DB.checklists.save(). Returns { queued: true, offline: true } when the
// device is offline, so the caller can tell the user it is saved and will send itself.
export async function saveChecklist(payload) {
  try {
    return await DB.checklists.save(payload);
  } catch (e) {
    const offline = (typeof DB !== 'undefined' && typeof DB.isOnline === 'function' && DB.isOnline() === false);
    if (offline) {
      // v136.8: only claim "queued" when the entry was really stored; a full device gets the
      // error path ("Could not submit checklist"), never a false "saved".
      if (!enqueue(payload)) {
        updateIndicator();
        const err = new Error('Offline and this device has no room to keep the checklist');
        err.storageFull = true;
        throw err;
      }
      updateIndicator();
      return { queued: true, offline: true };
    }
    throw e;   // a server "no" is still a real error the store must see
  }
}

// ── Replay ─────────────────────────────────────────────────────────────────

let _draining = false;

export async function drain() {
  if (_draining) return { drained: 0, remaining: pendingCount() };
  _draining = true;
  let drained = 0;
  let deadLettered = 0;
  try {
    const list = read();
    for (let i = 0; i < list.length; i++) {
      const entry = list[i];
      const key = outboxKey(entry);
      try {
        await DB.checklists.save(stripQueueMeta(entry));
        removeKey(key);
        drained++;
        onFiled(entry);
      } catch (e) {
        if (isAlreadyFiled(e)) {
          // Already on the server — the goal, reached. Not a failure.
          removeKey(key);
          drained++;
          onFiled(entry);
          continue;
        }
        const offline = (typeof DB !== 'undefined' && typeof DB.isOnline === 'function' && DB.isOnline() === false);
        if (offline) break;            // network is down again — leave the whole queue, do not penalise
        const attempts = bumpAttempts(key, e);
        if (attempts >= MAX_REPLAY_ATTEMPTS) { deadLetter(entry); deadLettered++; }
        break;                          // one server rejection at a time; retry on the next trigger
      }
    }
  } finally {
    _draining = false;
    updateIndicator();
  }
  return { drained, deadLettered, remaining: pendingCount() };
}

// When an entry finally lands, promote the device's own copy to synced so the
// dashboard stops showing it as outstanding (v130.38 made that state honest).
function onFiled(entry) {
  try {
    if (typeof window === 'undefined' || typeof window.Checklists === 'undefined') return;
    const mark = window.Checklists.helpers && window.Checklists.helpers.markLocalSynced;
    if (typeof mark === 'function') mark(entry.store, entry.date, entry.type);
  } catch (e) { /* cosmetic only — the server has the record either way */ }
}

// ── Indicator + triggers ───────────────────────────────────────────────────

function updateIndicator() {
  try {
    const el = document.getElementById('clOutboxIndicator');
    if (!el) return;
    const n = pendingCount();
    const dead = deadLetterCount();
    if (!n && !dead) { el.style.display = 'none'; return; }
    el.style.display = 'block';
    el.textContent = dead
      ? '⚠️ ' + dead + ' check' + (dead === 1 ? '' : 's') + ' could not be sent — tell your manager'
      : '⏳ ' + n + ' check' + (n === 1 ? '' : 's') + ' waiting to send';
  } catch (e) { /* no UI on this screen */ }
}

let _wired = false;

// Idempotent — safe to call on every boot.
export function initChecklistOutbox() {
  if (_wired) return;
  _wired = true;
  try {
    window.addEventListener('online', function () { drain(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') drain();
    });
  } catch (e) { /* non-browser context (tests) */ }
  updateIndicator();
  drain();
}
